/**
 * PromptPreviewModal.js
 *
 * Dry-run preview of a prompt after variable substitution.
 * Fetches GET /api/businesses/:id/prompt-preview?prompt_type=research
 * Shows: raw prompt, resolved prompt, variables table (resolved/empty),
 * and a copy-to-clipboard button. No AI call is made.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Copy, RefreshCw, Eye, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { authFetch, API } from "../utils/api";

const TYPE_LABELS = {
  research:        "Investigación",
  outreach:        "Outreach (emails)",
  site_generation: "Generación de sitio",
};

export default function PromptPreviewModal({
  businessId,
  promptType = "research",
  onClose,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch(
        `${API}/businesses/${businessId}/prompt-preview?prompt_type=${promptType}`
      );
      if (!res) return;
      if (!res.ok) { toast.error("No se pudo cargar la vista previa"); return; }
      setData(await res.json());
    } catch {
      toast.error("Error al cargar la vista previa");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [businessId, promptType]); // eslint-disable-line

  const copyResolved = () => {
    if (!data?.resolved_prompt) return;
    navigator.clipboard.writeText(data.resolved_prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="prompt-preview-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#FF4F00]/10 flex items-center justify-center">
              <Eye size={14} className="text-[#FF4F00]" />
            </div>
            <div>
              <h2 className="text-base font-black font-heading text-[#0A0A0A]">
                Vista previa del prompt
              </h2>
              <p className="text-xs text-gray-400">
                Así llegará el prompt de <b>{TYPE_LABELS[promptType] || promptType}</b> a la IA
                — sin gastar créditos.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={load}
              disabled={loading}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
              title="Recargar"
              data-testid="preview-reload-btn"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
              data-testid="preview-close-btn"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400 text-sm gap-2">
              <Loader2 size={16} className="animate-spin" /> Resolviendo variables…
            </div>
          ) : !data?.raw_prompt?.trim() ? (
            <EmptyPromptHint />
          ) : (
            <>
              <ContextSummary ctx={data.context} />
              <VariablesTable variables={data.variables || []} />
              <ResolvedPromptBlock
                resolved={data.resolved_prompt}
                onCopy={copyResolved}
                copied={copied}
              />
              <RawPromptBlock raw={data.raw_prompt} />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <p className="text-[11px] text-gray-400">
            No se envía ningún dato a la IA. Editá el prompt en <b>Settings</b> y volvé acá para verificar.
          </p>
          <button
            onClick={onClose}
            className="text-xs font-semibold text-gray-600 hover:text-gray-900 px-3 py-1.5"
            data-testid="preview-footer-close-btn"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function EmptyPromptHint() {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3 items-start">
      <AlertTriangle size={18} className="text-amber-600 shrink-0" />
      <div>
        <p className="text-sm font-semibold text-amber-900 mb-1">
          Este prompt está vacío en Settings.
        </p>
        <p className="text-xs text-amber-800">
          Ingresá a Settings → Prompt de investigación y escribí algo usando variables
          como <code className="bg-amber-100 px-1 rounded">{"{{empresa}}"}</code>,{" "}
          <code className="bg-amber-100 px-1 rounded">{"{{categoria}}"}</code> o{" "}
          <code className="bg-amber-100 px-1 rounded">{"{{campo.<id>}}"}</code>.
        </p>
      </div>
    </div>
  );
}

function ContextSummary({ ctx }) {
  if (!ctx) return null;
  const badges = [
    { on: !!ctx.business?.name, label: `Negocio: ${ctx.business?.name || "—"}` },
    { on: !!ctx.has_contact,    label: `Contacto: ${ctx.contact?.name || "—"}` },
    { on: !!ctx.has_research,   label: "Investigación previa" },
    { on: !!ctx.has_web_script, label: "Script web" },
  ];
  return (
    <div className="flex flex-wrap gap-1.5">
      {badges.map((b) => (
        <span
          key={b.label}
          className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${
            b.on
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-gray-50 border-gray-200 text-gray-400"
          }`}
        >
          {b.on ? <CheckCircle2 size={9} className="inline mb-0.5 mr-0.5" /> : null}
          {b.label}
        </span>
      ))}
    </div>
  );
}

function VariablesTable({ variables }) {
  if (variables.length === 0) {
    return (
      <div className="text-xs text-gray-400 italic">
        No hay variables en el prompt. Agregá algunas desde la lista de variables en Settings.
      </div>
    );
  }
  return (
    <div>
      <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">
        Variables detectadas ({variables.length})
      </h3>
      <div
        className="border border-gray-200 rounded-xl overflow-hidden"
        data-testid="preview-vars-table"
      >
        {variables.map((v) => (
          <div
            key={v.variable}
            className="grid grid-cols-[180px_1fr_70px] items-center gap-2 px-3 py-2 border-b border-gray-100 last:border-b-0 text-xs"
          >
            <code className="text-[11px] font-mono text-gray-800 bg-gray-50 px-1.5 py-0.5 rounded truncate">
              {v.variable}
            </code>
            <div className="text-gray-700 truncate" title={v.value}>
              {v.value || <span className="text-gray-300 italic">(vacío)</span>}
            </div>
            <span
              className={`text-[10px] font-semibold text-center px-1.5 py-0.5 rounded ${
                v.status === "resolved"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-700"
              }`}
            >
              {v.status === "resolved" ? "✓ OK" : "vacío"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResolvedPromptBlock({ resolved, onCopy, copied }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">
          Prompt final (lo que recibirá la IA)
        </h3>
        <button
          onClick={onCopy}
          className="text-[10px] text-gray-500 hover:text-gray-800 flex items-center gap-1"
          data-testid="preview-copy-btn"
        >
          <Copy size={10} /> {copied ? "¡Copiado!" : "Copiar"}
        </button>
      </div>
      <pre
        data-testid="preview-resolved-prompt"
        className="text-xs text-[#0A0A0A] bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-xl p-3 whitespace-pre-wrap font-mono max-h-64 overflow-y-auto"
      >
        {resolved}
      </pre>
    </div>
  );
}

function RawPromptBlock({ raw }) {
  return (
    <details className="group">
      <summary className="text-[10px] font-bold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700">
        Ver prompt original (con variables) ▸
      </summary>
      <pre
        data-testid="preview-raw-prompt"
        className="mt-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-xl p-3 whitespace-pre-wrap font-mono max-h-40 overflow-y-auto"
      >
        {raw}
      </pre>
    </details>
  );
}
